import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import { History, RotateCcw } from "lucide-react";

export default function VersionHistory({ open, onOpenChange, entity, entityId, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && entity && entityId) {
      api.get(`/versions/${entity}/${entityId}`).then((r) => setVersions(r.data)).catch(() => setVersions([]));
    }
  }, [open, entity, entityId]);

  const restore = async (vid) => {
    if (!(await confirmDialog({ title: "Restore this version?", description: "Your current version is saved to history first, so you can undo.", confirmText: "Restore" }))) return;
    setBusy(true);
    try {
      await api.post(`/versions/${entity}/${entityId}/restore/${vid}`);
      toast.success("Version restored");
      onRestored && onRestored();
      onOpenChange(false);
    } catch {
      toast.error("Could not restore this version");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4" />Version history</DialogTitle>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto space-y-2">
          {versions.length === 0 && (
            <p className="text-sm text-cass-slate py-6 text-center" data-testid="version-empty">
              No saved versions yet. A version is captured automatically each time you save.
            </p>
          )}
          {versions.map((v) => (
            <div key={v.id} data-testid={`version-${v.id}`} className="flex items-center justify-between p-3 rounded-md border border-border">
              <div>
                <div className="text-sm font-medium text-cass-navy">{v.name || "Version"}</div>
                <div className="text-xs text-cass-slate">
                  {new Date(v.saved_at).toLocaleString("en-GB")}{v.saved_by ? ` · ${v.saved_by}` : ""}{v.note ? ` · ${v.note}` : ""}
                </div>
              </div>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => restore(v.id)} data-testid={`restore-${v.id}`}>
                <RotateCcw className="w-4 h-4 mr-1" />Restore
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
